import { Modal } from "./modal";

interface QuestionDataPayload {
    answers: string;
    content: string;
}

interface RequestPayload {
    deploymentId?: string;
    questionData: QuestionDataPayload;
    questionId: string;
    userId: string;
}

interface ResponsePayload {
    result?: {
        endpoints?: {
            launch?: string;
        };
    };
}

class Panel {
    private areEventsListenersRegistered = false;
    private readonly button: HTMLButtonElement;
    private readonly container: HTMLDivElement | undefined;
    private readonly handleResizeAndScroll = this.updateModalScroll.bind(this);
    private iframe: HTMLIFrameElement | undefined;
    private isInitialized = false;
    private launchContent: string = "";
    private launchContext: string = "";
    private modal: HTMLDivElement | undefined;
    private originalContainerHeight = 0;

    constructor(
        button: HTMLButtonElement,
        readonly questionId: string,
        readonly userId: string,
        readonly deploymentId?: string,
    ) {
        this.button = button;

        const foundContainer = button.closest(
            ".studentQuestionBox.studentQuestionContent",
        );

        if (foundContainer instanceof HTMLDivElement) {
            this.container = foundContainer;
        }

        this.generateQuestionDataPayload();
        this.mount();
    }

    close() {
        // Remove the unused event listeners
        if (this.areEventsListenersRegistered) {
            window.removeEventListener("resize", this.handleResizeAndScroll);
            window.removeEventListener("scroll", this.handleResizeAndScroll);
            this.areEventsListenersRegistered = false;
        }

        // Hide the modal
        this.modal?.classList.add("sa-hide");

        // Restore the original container height if it was adjusted to fit the modal
        this.container?.style.setProperty(
            "min-height",
            `${this.originalContainerHeight}px`,
        );
    }

    private generateQuestionDataPayload() {
        const launchContextId = `Q${this.questionId}.studentAssistantLaunchContext`;
        const launchContextElement = document.getElementById(launchContextId);
        if (launchContextElement instanceof HTMLInputElement) {
            this.launchContext = launchContextElement.value.trim();
        }

        // Generate a copy of the question markup and filter out the extra content
        const foundQuestionBox = this.button.closest(
            ".studentQuestionBox.studentQuestionContent",
        );

        if (foundQuestionBox instanceof HTMLDivElement) {
            // Clone the original div
            // The 'true' argument means a deep clone (includes all children and their content)
            const clonedDiv = foundQuestionBox.cloneNode(true);

            if (clonedDiv instanceof HTMLDivElement) {
                // Query and remove elements from the cloned div
                clonedDiv
                    .querySelectorAll(".extraContent")
                    .forEach((element) => {
                        element.remove();
                    });

                const cleanedHtml = clonedDiv.outerHTML;

                let contentJson;
                try {
                    contentJson = JSON.stringify(cleanedHtml);
                } catch {
                    contentJson = "";
                }
                this.launchContent = contentJson;
            }
        }
    }

    private async getLaunchUrl() {
        const requestBody: RequestPayload = {
            questionData: {
                answers: this.launchContext,
                content: this.launchContent,
            },
            questionId: this.questionId,
            userId: this.userId,
        };

        // Add optional deploymentId
        if (this.deploymentId) {
            requestBody.deploymentId = this.deploymentId;
        }

        const url =
            "/web/Rest/StudentAssistant-MiddlewareRequest/middleware_prepare_request";

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody),
                credentials: "include",
            });

            // Check for HTTP errors (non-2xx status)
            if (!response.ok) {
                throw new Error("Internal Server Error");
            }

            const data: ResponsePayload = await response.json();
            const endpoint = data.result?.endpoints?.launch;

            return endpoint;
        } catch (error) {
            throw error; // Re-throw to allow callers to handle it
        }
    }

    private init() {
        this.getLaunchUrl()
            .then((launchUrl) => {
                if (this.iframe && launchUrl) {
                    this.iframe.src = launchUrl;
                    this.isInitialized = true;
                }
            })
            .catch(() => {
                this.close();
                new Modal(this.button);
            });
    }

    private mount() {
        const modal = document.createElement("div");
        modal.id = `sa-modal-${this.questionId}`;
        modal.tabIndex = 0;

        modal.classList.add("sa-modal");
        modal.setAttribute("role", "dialog");
        modal.setAttribute("aria-modal", "true");
        modal.setAttribute("aria-labelledby", `sa-heading-${this.questionId}`);

        // Screen reader heading
        const screenReaderHeading = Object.assign(
            document.createElement("h2"),
            {
                id: `sa-heading-${this.questionId}`,
                textContent: "Student assistant chat",
                className: "sr-only",
            },
        );
        modal.appendChild(screenReaderHeading);

        // Minimize button
        const minimizeButton = Object.assign(document.createElement("button"), {
            type: "button",
            className: "sa-minimize",
            ariaLabel: "Minimize chat",
            textContent: "-",
        });
        minimizeButton.addEventListener("click", () => {
            this.button.focus();
            this.close();
        });
        modal.appendChild(minimizeButton);

        // Iframe
        const iframe = Object.assign(document.createElement("iframe"), {
            id: `sa-modal-frame-${this.questionId}`,
            title: "Student Assist",
            tabIndex: 0,
            src: `${__PATH__}loading${__VERSION__}.html`, // Ensure these global variables are defined
            className: "sa-iframe",
        });
        iframe.addEventListener("load", () => {
            console.log(`iframe loaded for ${this.questionId}`);
        });
        modal.appendChild(iframe);

        // Mount into the DOM
        this.container?.appendChild(modal);

        this.iframe = iframe;
        this.modal = modal;

        this.originalContainerHeight = this.container?.offsetHeight ?? 0;
    }

    open() {
        // Register event listeners if not yet registered
        if (!this.areEventsListenersRegistered) {
            window.addEventListener("resize", this.handleResizeAndScroll);
            window.addEventListener("scroll", this.handleResizeAndScroll);
            this.areEventsListenersRegistered = true;
        }

        // Attempt init if not yet initialized
        if (!this.isInitialized) {
            this.init();
        }

        // Show the modal
        this.modal?.classList.remove("sa-hide");

        // Adjust container size to fit the modal if needed
        const containerHeight = this.container?.offsetHeight ?? 0;
        const modalHeight = this.modal?.offsetHeight ?? 0;

        if (containerHeight < modalHeight) {
            this.container?.style.setProperty("min-height", `${modalHeight}px`);
        }

        // Move focus in to the modal
        this.modal?.focus();
    }

    private updateModalScroll() {
        if (!this.container || !this.modal) return; // Exit if elements are missing

        const containerRect = this.container.getBoundingClientRect();
        const modalHeight = this.modal.offsetHeight;

        const spaceFromBottom = containerRect.bottom - window.innerHeight;
        const offset = Math.max(
            0,
            Math.min(
                this.container.offsetHeight - modalHeight,
                spaceFromBottom,
            ),
        );

        // Adjust the modal's y-position to stay within the view as the user scrolls
        this.modal?.style.setProperty("transform", `translateY(-${offset}px)`);
    }
}

export { Panel };
